import { CaretLeft, CaretRight, CornersOut } from '@phosphor-icons/react';
import { cn, Inline, Modal, ModalContent, Stack } from '@reelplicate/ui';
import type { KeyboardEvent } from 'react';

import type { SlideText } from './types';

type ViewerProps = {
  /** The slides' images, in order. */
  slides: string[];
  /** What the carousel is called, for the images' alt text. */
  title: string;
  /** The slide shown (0-based). */
  index: number;
  onIndexChange: (index: number) => void;
};

/**
 * ← / → step through the slides. Handled here and stopped, so they don't
 * also step the drawer behind to the next asset.
 */
function stepKeys(
  { index, slides, onIndexChange }: Omit<ViewerProps, 'title'>,
  event: KeyboardEvent,
) {
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
  event.stopPropagation();
  const next = index + (event.key === 'ArrowLeft' ? -1 : 1);
  if (next >= 0 && next < slides.length) onIndexChange(next);
}

const arrow =
  'focus-visible:shadow-focus-ring absolute top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-white/85 shadow-sm outline-none hover:bg-white';

/** One slide at a time, letterboxed on black, with dots and arrows. */
export function Slides({
  slides,
  title,
  index,
  onIndexChange,
  large = false,
  className,
}: ViewerProps & { large?: boolean; className?: string }) {
  const current = Math.min(index, slides.length - 1);
  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label={`${title}, slide ${current + 1} of ${slides.length}`}
      onKeyDown={(e) => stepKeys({ index: current, slides, onIndexChange }, e)}
      className={cn('relative overflow-hidden bg-black', className)}
    >
      <img
        src={slides[current]}
        alt={`Slide ${current + 1} of ${title}`}
        // Slides aren't always 9:16: show the whole image.
        className="absolute inset-0 size-full object-contain"
      />
      <Inline
        gap={1}
        align="center"
        wrap
        className={cn('absolute inset-x-0 px-2', large ? 'bottom-3' : 'bottom-2')}
      >
        {slides.map((url, i) => (
          <button
            key={url}
            type="button"
            aria-label={`Show slide ${i + 1}`}
            aria-current={i === current || undefined}
            onClick={() => onIndexChange(i)}
            className={cn(
              'rounded-full shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-white',
              large ? 'size-2' : 'size-1.5',
              i === current ? 'bg-white' : 'bg-white/50',
            )}
          />
        ))}
      </Inline>
      {current > 0 && (
        <button
          type="button"
          aria-label="Previous slide"
          onClick={() => onIndexChange(current - 1)}
          className={cn(arrow, large ? 'left-3 size-9' : 'left-1.5 size-6')}
        >
          <CaretLeft
            aria-hidden
            className={cn('text-text-primary', large ? 'size-5' : 'size-3.5')}
          />
        </button>
      )}
      {current < slides.length - 1 && (
        <button
          type="button"
          aria-label="Next slide"
          onClick={() => onIndexChange(current + 1)}
          className={cn(arrow, large ? 'right-3 size-9' : 'right-1.5 size-6')}
        >
          <CaretRight
            aria-hidden
            className={cn('text-text-primary', large ? 'size-5' : 'size-3.5')}
          />
        </button>
      )}
    </div>
  );
}

/** The drawer's preview of a carousel: swipe through it in place, or open it large. */
export function CarouselPreview({
  onExpand,
  ...props
}: ViewerProps & { onExpand: () => void }) {
  const current = Math.min(props.index, props.slides.length - 1);
  return (
    <div className="relative h-80 w-45 shrink-0 overflow-hidden rounded-md">
      <Slides {...props} className="size-full" />
      <span className="text-caption absolute top-1.5 left-1.5 rounded-full bg-black/60 px-2 py-0.5 text-white tabular-nums">
        {current + 1} / {props.slides.length}
      </span>
      <button
        type="button"
        aria-label="View slides larger"
        onClick={onExpand}
        className="focus-visible:shadow-focus-ring absolute top-1.5 right-1.5 flex size-6 items-center justify-center rounded-full bg-black/60 text-white outline-none hover:bg-black/75"
      >
        <CornersOut aria-hidden className="size-3.5" />
      </button>
    </div>
  );
}

/** The carousel large, with what the slide shown says under it. */
export function CarouselModal({
  open,
  onOpenChange,
  text,
  ...props
}: ViewerProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What each slide says, when it was read. */
  text?: SlideText[];
}) {
  const current = Math.min(props.index, props.slides.length - 1);
  const said = text?.find((s) => s.slide === current + 1);
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="lg"
        title={props.title}
        description={`Slide ${current + 1} of ${props.slides.length}`}
        // The drawer behind listens for ← / → too; this modal keeps them.
        onKeyDown={(e) => stepKeys({ ...props, index: current }, e)}
      >
        <Stack gap={4}>
          <Slides {...props} large className="h-[min(70dvh,640px)] rounded-md" />
          {said && (
            <Stack gap={1} aria-live="polite">
              {said.text && (
                <p className="text-body-md text-text-primary whitespace-pre-line">
                  {said.text}
                </p>
              )}
              {said.context && (
                <p className="text-body-sm text-text-tertiary italic">{said.context}</p>
              )}
            </Stack>
          )}
        </Stack>
      </ModalContent>
    </Modal>
  );
}
